<script setup lang="ts">
/**
 * Gedeelde schil rond elk formulierveld: label, prefix/suffix, fouten, voorbeeldtekst
 * en info. De leaf-componenten (VTextInput, VSelect, …) leveren enkel het control.
 *
 * Eén look, ongeacht de wrapper (.edit-create, .administration-edit-create, modal of
 * niets): de wortel draagt `.v-field` i.p.v. `.v-input`, dus de contextregels in
 * scaffold.scss grijpen er niet op aan. De look zelf staat in _v-field.scss en is die
 * van VInput in `.edit-create`. De markup volgt die van VInput, want _v-field.scss
 * stylet op deze structuur.
 *
 * Ondermarge enkel binnen .edit-create / .administration-edit-create, zoals VInput (zie
 * laag 3 in _v-field.scss); elders zet het formulier de ruimte (`flex flex-col gap-4`).
 */
import { computed, onMounted, ref, useSlots } from 'vue'
import ToggleVatLabel from '@/Components/Form/Toggle-vat-label.vue'
import type { FieldLayout } from './types'
import { fieldLayoutClasses } from './useFormField'

const props = withDefaults(
    defineProps<{
        label?: string | null
        /** Id van het control, voor `<label for>`. */
        for?: string | null
        required?: boolean
        example?: string | null
        /** SECURITY: v-html. Nooit user-controllable data zonder escapeHtml(). */
        info?: string | null
        errors?: string[]
        /** Grijze hint vóór de fouten (uploadlimieten van de dropzone). */
        hint?: string | null
        layout?: FieldLayout
        /**
         * Zonder label tóch de labelkolom vrijhouden, zodat het control op één lijn staat
         * met de velden erboven. Enkel voor VCheckbox (zijn label staat naast het vakje).
         * Andere velden zonder label vullen de volle breedte van hun plek, bv. in een
         * documentregel.
         */
        indent?: boolean
        hasPrefixSelect?: boolean
        labelClasses?: string
        inputClasses?: string
        exampleClasses?: string
        errorClasses?: string
        infoClasses?: string
        suffixClasses?: string
        vatButton?: boolean
        vatValue?: boolean
    }>(),
    {
        label: null,
        for: null,
        required: false,
        example: null,
        info: null,
        errors: () => [],
        hint: null,
        layout: 'horizontal',
        indent: false,
        hasPrefixSelect: false,
        labelClasses: '',
        inputClasses: '',
        exampleClasses: '',
        errorClasses: '',
        infoClasses: '',
        suffixClasses: '',
        vatButton: false,
        vatValue: false,
    }
)

const emit = defineEmits<{
    (e: 'vatChanges', value: 'vat_included' | 'vat_excluded'): void
}>()

const slots = useSlots()

/**
 * Zonder label is er geen labelkolom: het veld gedraagt zich als `stacked` en het control
 * vult de volle breedte. Dat is wat documentregels verwachten (daar zette VInput de
 * breedte met `input-classes="lg:!col-span-3"`). Met `indent` blijft de grid staan.
 */
const effectiveLayout = computed<FieldLayout>(() =>
    props.label == null && !props.indent ? 'stacked' : props.layout
)

const indentControl = computed<boolean>(
    () => props.label == null && props.indent && effectiveLayout.value !== 'stacked'
)

const hasPrefix = computed<boolean>(() => !!slots.prefix)
const hasSuffix = computed<boolean>(() => !!slots.suffix)

const prefixClasses = computed<string>(() => `prefix ${props.hasPrefixSelect ? 'p-0' : 'px-2'}`)

const vatOption = ref<'vat_included' | 'vat_excluded'>('vat_excluded')

/**
 * Btw-toggle in het label. Bij mount emit hij meteen de beginstand: schermen zoals de
 * productprijs rekenen daarop om hun state te initialiseren.
 */
function toggleVatOption(initiate = false) {
    if (initiate) {
        vatOption.value = props.vatValue ? 'vat_excluded' : 'vat_included'
    } else {
        vatOption.value = vatOption.value === 'vat_included' ? 'vat_excluded' : 'vat_included'
    }
    emit('vatChanges', vatOption.value)
}

onMounted(() => {
    if (props.vatButton) {
        toggleVatOption(true)
    }
})

defineExpose({ hasPrefix, hasSuffix })
</script>

<template>
    <div :class="fieldLayoutClasses(effectiveLayout)">
        <label v-if="label != null" :for="props.for ?? undefined" :class="labelClasses">
            <toggle-vat-label
                v-if="vatButton"
                @click="toggleVatOption(false)"
                :vatOption="vatOption"
            ></toggle-vat-label>

            {{ $t(label) }}
            <span v-if="required" class="text-danger"> * </span>
            <!-- Kleine actie naast het label, bv. een reset-icoon (#6072). -->
            <span v-if="$slots['label-append']" class="ml-1 inline-flex align-middle">
                <slot name="label-append"></slot>
            </span>
        </label>
        <div
            :class="[
                'flex !col-span-3 lg:!col-span-2',
                // `!`: `lg:!col-span-2` zet via de shorthand ook de startkolom.
                { 'lg:!col-start-2': indentControl },
                inputClasses,
            ]"
        >
            <span v-if="hasPrefix" :class="prefixClasses">
                <slot name="prefix"></slot>
            </span>

            <slot></slot>

            <span
                v-if="hasSuffix"
                :class="[
                    'suffix inline-flex items-center px-2 text-gray-900 bg-gray-200 border border-l-0 border-gray-300 rounded-r',
                    suffixClasses,
                ]"
            >
                <slot name="suffix"></slot>
            </span>
        </div>

        <slot name="after-control"></slot>

        <!-- Rijke hint onder het veld (met links, bv. "... of maak een nieuwe klant aan"),
             zelfde plek als `example`. Eigen klasse: de .v-field-regels voor span/div (vet,
             basis-1/4, col-span-2) slaan er niet op. De keuzelijsten (klant, project, product,
             …) bouwden dit elk zelf, telkens met andere marges. -->
        <div
            v-if="$slots.hint"
            :class="[
                'v-field__hint col-span-3 mt-1 flex flex-wrap gap-x-1 text-[13px] font-light italic text-secondary-text lg:col-span-2 lg:col-start-2 [&_span]:text-inherit',
                exampleClasses,
            ]"
        >
            <slot name="hint"></slot>
        </div>

        <span v-if="hint" :class="['example', exampleClasses]">{{ hint }}</span>

        <!-- Meerdere fouten achter elkaar op één regel, niet onder elkaar. -->
        <span v-if="errors.length" :class="['error-message text-danger', errorClasses]">{{
            errors.join(' ')
        }}</span>
        <span v-if="example" :class="['example', exampleClasses]">{{ example }}</span>
        <!-- SECURITY: info gebruikt v-html voor HTML-opmaak in vertalingen (bijv. <code>, <div class="pill">).
             Geef NOOIT user-controllable data door aan info zonder escapeHtml(). -->
        <span v-if="info" :class="['info', infoClasses]" v-html="info"></span>
    </div>
</template>

<style scoped lang="scss">
.prefix {
    @apply inline-flex items-center text-gray-900 bg-gray-200 border border-r-0 border-gray-300 rounded-l font-normal;
    flex-basis: 0;
    max-height: 38px;
}
</style>
